<template>
  <div class="relative">
    <!-- Newsletter Info -->
    <div
      :class="
        previewOpen ? 'absolute opacity-0 pointer-events-none' : 'opacity-100'
      "
      class="z-10"
    >
      <div class="">
        <div class="border-b border-truegray-300 pb-4">
          <h2>{{ config.newsletter.title }}</h2>
          <p class="text-sm text-truegray-500">
            {{ config.newsletter.subject }}
          </p>
          <p
            class="cursor-pointer underline text-xs text-primary mt-4 mb-2"
            @click="previewNewsletter"
          >
            Preview Newsletter
          </p>
        </div>

        <div class="border-b border-truegray-300 py-4">
          <div class="flex items-center space-x-2 pb-2">
            <p class="text-xs text-truegray-400">Sent on:</p>
            <p>{{ config.newsletter.sent_at_ui }}</p>
          </div>

          <div class="flex items-center space-x-2 pb-2">
            <p class="text-xs text-truegray-400">Sent to:</p>
            <p
              v-if="newsletterMailLists"
              class="flex items-center flex-wrap gap-2 px-2 my-2"
            >
              <span
                v-for="(list, i) in newsletterMailLists"
                :key="i"
                class="py-1 px-2 rounded-xl mr-2 border border-primary text-xs text-primary group hover:bg-primary hover:text-white transition-all duration-300 ease-in-out relative"
              >
                <span class="group-hover:opacity-0">{{ list.name }}</span>
                <span
                  :style="{ fontSize: '9px' }"
                  class="group-hover:opacity-100 capitalize opacity-0 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                >
                  {{ list.type || 'CW' }} list
                </span>
              </span>
            </p>
          </div>

          <!-- Toggle Posts button -->
          <div
            class="flex items-center cursor-pointer pb-6 mt-4"
            @click="openPosts"
          >
            <div
              class="w-5 h-5 mr-4 flex items-center justify-center bg-primary hover:bg-transparent rounded border border-primary transition-all duration-300 ease-in-out text-white hover:text-cw-700 cursor-pointer"
            >
              <svg
                v-if="!postsOpen"
                xmlns="http://www.w3.org/2000/svg"
                class="h-3 w-3"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <p class="w-full m-0 text-truegray-700">Posts:</p>
          </div>
          <!-- Posts List -->
          <div
            v-if="postsOpen"
            class="transition-all duration-500 ease-in-out overflow-hidden relative"
          >
            <NewsletterBuilderPosts :config="postsConfig" />
          </div>
        </div>

        <!-- Stats/Percentages Toggler -->
        <div
          :class="statsLoaded ? '' : 'cursor-not-allowed pointer-events-none'"
          class="flex items-center justify-between pt-6 transition-all cursor-pointer group"
          @click="statsOpen = !statsOpen"
        >
          <h3 class="mb-0 relative flex items-center">
            Newsletter Stats
            <ProTag
              :style="[{ top: '-5px' }, { right: '-55px' }]"
              class="absolute"
            />
          </h3>
          <div
            v-if="statsLoaded"
            :class="statsOpen ? 'rotate-90 text-primary' : ''"
            class="group-hover:text-primary transition-all"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="w-5 h-5 transition-all"
            >
              <path
                fill-rule="evenodd"
                d="M10.21 14.77a.75.75 0 01.02-1.06L14.168 10 10.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clip-rule="evenodd"
              />
              <path
                fill-rule="evenodd"
                d="M4.21 14.77a.75.75 0 01.02-1.06L8.168 10 4.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div v-else class="h-12 flex flex-col justify-center items-center">
            <lottie :options="loadingAnimationOptions" />
          </div>
        </div>

        <!-- Stats Wrapper -->
        <div
          :style="statsOpen ? {} : { height: '0px' }"
          class="w-full overflow-hidden transition-all h-auto"
        >
          <div
            id="statsContainer"
            :class="statsOpen ? 'opacity-100' : 'opacity-0'"
            class="w-full overflow-hidden transition-all duration-300"
          >
            <!-- List toggles -->
            <div
              v-if="
                showListToggle &&
                newsletterMailLists &&
                stats.data.stats.current_campaign_ids.length
              "
              class="flex flex-col space-y-2 mt-4"
            >
              <p class="flex items-center">
                <span
                  class="cursor-pointer py-1 px-2 rounded-xl mr-2 border border-primary text-xs group hover:bg-primary hover:text-white transition-all duration-300 ease-in-out relative"
                  :class="
                    isActiveCampaign()
                      ? 'text-white bg-primary'
                      : 'text-primary'
                  "
                  @click="findNewsletterStats()"
                >
                  All
                </span>
                <span
                  v-for="(list, i) in newsletterMailLists"
                  :key="i"
                  class="cursor-pointer py-1 px-2 rounded-xl mr-2 border border-primary text-xs group hover:bg-primary hover:text-white transition-all duration-300 ease-in-out relative"
                  :class="
                    isActiveCampaign(list.campaignId)
                      ? 'text-white bg-primary'
                      : 'text-primary'
                  "
                  @click="findNewsletterStats(list.campaignId)"
                >
                  <span class="">{{ list.name }}</span>
                </span>
              </p>
            </div>
            <div class="flex">
              <!-- Stat #s -->
              <div
                class="w-2/3 pb-6 pr-8 flex flex-col justify-center"
                :class="proData ? '' : 'mt-6'"
              >
                <div>
                  <div class="flex items-center space-x-2 pb-1">
                    <p class="text-xs text-truegray-400">Total Sent:</p>
                    <p>{{ stats.data.stats.total_sent }}</p>
                  </div>

                  <div class="flex items-center space-x-2 pb-1">
                    <p class="text-xs text-truegray-400">Total Opened:</p>
                    <p>{{ stats.data.stats.total_opened }}</p>
                  </div>

                  <!--
                    <div class="flex items-center space-x-2 pb-1">
                    <p class="text-xs text-truegray-400">Total Clicked:</p>
                    <p>{{ stats.data.stats.total_clicked }}</p>
                    </div> 
                  -->

                  <p
                    v-if="proData"
                    class="text-xs text-truegray-400 italic mt-4"
                  >
                    The feedback numbers are based on the global averages for
                    education email stats. If you think the number is low, you
                    might actually be doing better than you thought!
                  </p>
                  <p v-else class="text-xs text-truegray-400 italic mt-4">
                    Upgrade to the pro plan to get a full breakdown of your
                    newsletter stats. Find out your open rates, click rates, and
                    even view each individual subscriber's interactions with
                    your newsletters.
                  </p>
                </div>
              </div>
              <!-- Graphs -->
              <div class="w-1/3">
                <div
                  v-if="statsOpen"
                  class="flex space-x-6 my-8 justify-center"
                >
                  <div class="">
                    <div class="mb-2">
                      <vue-ellipse-progress
                        :progress="stats.data.stats.opened_percent"
                        v-bind="opensEllipseOptions"
                      >
                        <template #legend-value>
                          <span slot="legend-value">%</span>
                        </template>
                        <template #legend-caption>
                          <p class="text-xs text-truegray-500 italic">Opens</p>
                        </template>
                      </vue-ellipse-progress>
                    </div>
                    <p
                      :style="{ color: opensColour }"
                      class="text-center text-xs font-bold"
                    >
                      {{ opensText }}
                    </p>
                  </div>

                  <!--
                    <div class="">
                    <div class="mb-2">
                    <vue-ellipse-progress
                    :progress="stats.data.stats.clicked_percent"
                    v-bind="clicksEllipseOptions"
                    >
                    <template #legend-value>
                    <span slot="legend-value">%</span>
                    </template>
                    <template #legend-caption>
                    <p class="text-xs text-truegray-500 italic">Clicks</p>
                    </template>
                    </vue-ellipse-progress>
                    </div>
                    <p
                    :style="{ color: clicksColour }"
                    class="text-center text-xs font-bold"
                    >
                    {{ clicksText }}
                    </p>
                    </div> 
                  -->
                </div>
              </div>
            </div>
            <!-- Individual email Stats -->
            <div
              v-if="statsLoaded && !isCateRegionalAdmin"
              class="flex flex-col space-y-4 pt-4 relative max-h-96 overflow-scroll no-scrollbar cw-newsletter-table-wrapper"
            >
              <div class="w-full">
                <div class="w-full mb-2">
                  <div class="flex justify-between items-center w-full">
                    <div
                      class="w-3/4 text-xs font-bold text-truegray-500 pl-2 sticky top-0"
                    >
                      Sent to
                    </div>
                    <div
                      class="w-1/4 text-xs font-bold text-truegray-500 sticky top-0 text-center"
                    >
                      Opened
                    </div>
                    <!--
                      <div
                      class="w-1/4 text-xs font-bold text-truegray-500 sticky top-0 text-center"
                      >
                      Clicked
                      </div> 
                    -->
                  </div>
                </div>
                <div class="relative w-full">
                  <DynamicScroller
                    :items="campaigns"
                    :min-item-size="12"
                    key-field="contact_id"
                    class="max-h-20"
                  >
                    <template #default="{ item, index, active }">
                      <DynamicScrollerItem
                        :item="item"
                        :active="active"
                        :data-index="index"
                      >
                        <div
                          class="flex justify-between items-center w-full transition-all hover:bg-truegray-100"
                          :class="proData ? '' : 'opacity-80'"
                        >
                          <div
                            :class="[
                              'w-3/4 pl-2 py-1',
                              proData ? '' : 'filter blur-sm',
                            ]"
                            class="text-truegray-500"
                          >
                            {{ item.email }}
                          </div>
                          <div :class="['w-1/4 py-1']">
                            <div
                              class="flex items-center justify-center w-full"
                            >
                              <span
                                v-if="item.opened_at"
                                class="text-green-400"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  class="w-4 h-4"
                                >
                                  <path
                                    fill-rule="evenodd"
                                    d="M2.106 6.447A2 2 0 001 8.237V16a2 2 0 002 2h14a2 2 0 002-2V8.236a2 2 0 00-1.106-1.789l-7-3.5a2 2 0 00-1.788 0l-7 3.5zm1.48 4.007a.75.75 0 00-.671 1.342l5.855 2.928a2.75 2.75 0 002.46 0l5.852-2.926a.75.75 0 10-.67-1.342l-5.853 2.926a1.25 1.25 0 01-1.118 0l-5.856-2.928z"
                                    clip-rule="evenodd"
                                  />
                                </svg>
                              </span>
                              <span v-else class="text-red-400">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  class="w-5 h-5"
                                >
                                  <path
                                    d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
                                  />
                                </svg>
                              </span>
                            </div>
                          </div>
                          <!--
                            <div :class="['w-1/4 py-1']">
                            <div
                            class="flex items-center justify-center w-full"
                            >
                            <span
                            v-if="item.clicked_at"
                            class="text-green-400"
                            >
                            <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            class="w-4 h-4"
                            >
                            <path
                            fill-rule="evenodd"
                            d="M10 1a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5A.75.75 0 0110 1zM5.05 3.05a.75.75 0 011.06 0l1.062 1.06A.75.75 0 116.11 5.173L5.05 4.11a.75.75 0 010-1.06zm9.9 0a.75.75 0 010 1.06l-1.06 1.062a.75.75 0 01-1.062-1.061l1.061-1.06a.75.75 0 011.06 0zM3 8a.75.75 0 01.75-.75h1.5a.75.75 0 010 1.5h-1.5A.75.75 0 013 8zm11 0a.75.75 0 01.75-.75h1.5a.75.75 0 010 1.5h-1.5A.75.75 0 0114 8zm-6.828 2.828a.75.75 0 010 1.061L6.11 12.95a.75.75 0 01-1.06-1.06l1.06-1.06a.75.75 0 011.06 0zm3.594-3.317a.75.75 0 00-1.37.364l-.492 6.861a.75.75 0 001.204.65l1.043-.799.985 3.678a.75.75 0 001.45-.388l-.978-3.646 1.292.204a.75.75 0 00.74-1.16l-3.874-5.764z"
                            clip-rule="evenodd"
                            />
                            </svg>
                            </span>
                            <span v-else class="text-red-400">
                            <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            class="w-4 h-4"
                            >
                            <path
                            d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
                            />
                            </svg>
                            </span>
                            </div>
                            </div> 
                          -->
                        </div>
                      </DynamicScrollerItem>
                    </template>
                  </DynamicScroller>
                  <div
                    v-if="!proData"
                    class="flex flex-col items-center justify-center space-y-4 border border-truegray-300 bg-white rounded px-6 py-4 upgrade-overlay bg-opacity-60"
                  >
                    <h3 class="text-lg m-0">Go pro to unlock advanced stats</h3>
                    <button
                      class="btn btn-cw-900 upgrade-button py-2 m-0"
                      @click="handleUpgrade"
                    >
                      Upgrade to Pro
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview -->
    <div
      :class="
        previewOpen ? 'opacity-100' : 'opacity-0 pointer-events-none absolute'
      "
      class="w-full z-20 transition-all bg-white"
    >
      <p
        class="cursor-pointer underline text-xs text-primary mb-6 flex items-center space-x-2"
        @click="previewNewsletter"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          class="w-5 h-5"
        >
          <path
            fill-rule="evenodd"
            d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
            clip-rule="evenodd"
          />
        </svg>
        Back to stats
      </p>
      <NewsletterBuilder v-if="previewOpen" :config="previewConfig" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { EventBus } from '../../../../../../js/plugins/event-bus.js'
import * as loadingAnimation from '../../../../../../shared/components/loading-dark.json'
import { VueEllipseProgress } from 'vue-ellipse-progress'
import NewsletterBuilder from '../NewsletterBuilder.vue'
import NewsletterBuilderPosts from '../NewsletterBuilderPosts.vue'
import ProTag from '../../../components/ProTag.vue'

export default {
  components: {
    VueEllipseProgress,
    NewsletterBuilderPosts,
    NewsletterBuilder,
    ProTag,
  },

  props: {
    config: { default: null },
  },

  data() {
    return {
      loadingAnimationOptions: {
        animationData: loadingAnimation.default,
      },

      loadingAnimate: false,
      statsLoaded: false,
      // newsletterMailLists: null,
      newsletterPosts: [],
      postsConfig: {},
      previewConfig: {
        build: false,
        presetData: {
          newsletterTitle: this.config.newsletter.title,
          newsletterBlurb: this.config.newsletter.blurb,
          newsletterPosts: [],
        },
      },

      // Pre API call data for non pro
      stats: {
        data: {
          stats: {
            total_sent: '?',
            total_opened: '?',
            total_clicked: '?',
            opened_percent: 100,
            clicked_percent: 100,
            current_campaign_ids: [],
          },

          campaign: [
            {
              contact_id: 0,
              email: 'example-subscriber@careerwise.school',
              opened_at: false,
              clicked_at: false,
            },
            {
              contact_id: 1,
              email: 'example-subscriber2@careerwise.school',
              opened_at: true,
              clicked_at: false,
            },
            {
              contact_id: 2,
              email: 'example-subscriber3@careerwise.school',
              opened_at: true,
              clicked_at: true,
            },
            {
              contact_id: 3,
              email: 'example-subscriber4@careerwise.school',
              opened_at: false,
              clicked_at: false,
            },
          ],
        },
      },

      postsOpen: false,
      postsLoading: false,

      statsOpen: false,
      statsHeight: '0px',

      previewOpen: false,
      previewHeight: '433px',

      proData: false,

      // ellipse data
      opensColour: '#FF4778',
      opensText: 'Go Pro to unlock!',
      clicksColour: '#6F36BC',
      clicksText: 'Go Pro to unlock!',

      opensEllipseOptions: {
        'color': '#FF4778',
        'empty-color': '#D4D4D4',
        'size': 120,
        'thickness': 5,
        'empty-thickness': 3,
        'line-mode': 'out 5',
        'animation': 'default 1500 250',
        'font-size': '1.25rem',
        'font-color': '#FF4778',
      },

      clicksEllipseOptions: {
        'color': '#6F36BC',
        'empty-color': '#D4D4D4',
        'size': 120,
        'thickness': 5,
        'empty-thickness': 3,
        'line-mode': 'out 5',
        'animation': 'default 1500 250',
        'font-size': '1.25rem',
        'font-color': '#6F36BC',
      },
    }
  },

  computed: {
    ...mapGetters({
      settings: 'shared/getSettings',
      isCateRegionalAdmin: 'shared/isCateRegionalAdmin',
      getMailListsByHailIds: 'careerwise/getMailListsByHailIds',
      mapCampaignIdsToMailLists: 'careerwise/mapCampaignIdsToMailLists',
    }),

    newsletterMailLists() {
      const campaignIds = JSON.parse(this.config.newsletter.hail_campaign_id)
      const listsSentTo = this.getMailListsByHailIds(
        this.config.newsletter.information.mail_lists,
      )
      return this.mapCampaignIdsToMailLists(campaignIds, listsSentTo)
    },

    campaigns() {
      return this.stats?.data?.campaign || []
    },

    showListToggle() {
      const campaigns = JSON.parse(this.config.newsletter.hail_campaign_id)
      return campaigns.length > 1
    },
  },

  watch: {
    stats: {
      handler: function (val) {
        this.handleStatRendering(val)
      },

      deep: true,
    },
  },

  async mounted() {
    // await this.getNewsletterMailLists(
    //     this.config.newsletter.information.mail_lists
    // );
    await this.getPostsFromSentNewsletter({
      post_ids: this.config.newsletter.information.post_ids,
    })

    if (this.settings.isPro) {
      this.findNewsletterStats()
    } else {
      this.checkStatsHeight()
      this.$set(this, 'statsLoaded', true)
    }
  },

  methods: {
    ...mapActions({
      dispatchGetHailCampaign: 'careerwise/dispatchGetHailCampaign',
      dispatchGetMailLists: 'careerwise/dispatchGetMailLists',
      dispatchGetPostsFromSentNewsletter:
        'careerwise/dispatchGetPostsFromSentNewsletter',
    }),

    async findNewsletterStats(id = null) {
      this.$set(this, 'statsLoaded', false)

      let payload = {
        hail_campaign_id: this.config.newsletter.hail_campaign_id,
      }
      if (id) {
        payload.hail_campaign_id = JSON.stringify([id])
      }
      const stats = await this.dispatchGetHailCampaign(payload)

      this.$set(this, 'stats', stats)

      this.$set(this, 'statsLoaded', true)
    },

    async getNewsletterMailLists(ids) {
      this.$set(this, 'newsletterMailLists', this.getMailListsByHailIds(ids))
    },

    previewNewsletter() {
      this.$set(this, 'previewOpen', !this.previewOpen)
    },

    openPosts() {
      this.$set(this, 'postsLoading', true)
      this.$set(this, 'postsConfig', {
        posts: this.newsletterPosts,
        build: false,
      })
      this.postsOpen = !this.postsOpen
      this.$set(this, 'postsLoading', false)
    },

    async getPostsFromSentNewsletter(payload) {
      const response = await this.dispatchGetPostsFromSentNewsletter(payload)
      this.$set(this, 'newsletterPosts', response.data.posts)
      this.$set(
        this.previewConfig.presetData,
        'newsletterPosts',
        response.data.posts,
      )
    },

    handleStatRendering(stats) {
      const great = '#31C48D'
      const good = '#4ADE80'
      const average = '#FACC15'
      const bad = '#38BDF8'

      // Open Rates
      const openPercent = stats.data.stats.opened_percent
      if (openPercent <= 15) {
        this.$set(this, 'opensColour', bad)
        this.$set(this.opensEllipseOptions, 'color', bad)
        this.$set(this.opensEllipseOptions, 'font-color', bad)
        this.$set(this, 'opensText', 'Could be better...')
      } else if (openPercent > 15 && openPercent <= 20) {
        this.$set(this, 'opensColour', average)
        this.$set(this.opensEllipseOptions, 'color', average)
        this.$set(this.opensEllipseOptions, 'font-color', average)
        this.$set(this, 'opensText', 'Not bad')
      } else if (openPercent > 20 && openPercent <= 30) {
        this.$set(this, 'opensColour', good)
        this.$set(this.opensEllipseOptions, 'color', good)
        this.$set(this.opensEllipseOptions, 'font-color', good)
        this.$set(this, 'opensText', 'Great')
      } /* it is greater than 30 */ else {
        this.$set(this, 'opensColour', great)
        this.$set(this.opensEllipseOptions, 'color', great)
        this.$set(this.opensEllipseOptions, 'font-color', great)
        this.$set(this, 'opensText', 'Amazing!')
      }

      const clickPercent = stats.data.stats.clicked_percent
      if (clickPercent <= 2) {
        this.$set(this, 'clicksColour', bad)
        this.$set(this.clicksEllipseOptions, 'color', bad)
        this.$set(this.clicksEllipseOptions, 'font-color', bad)
        this.$set(this, 'clicksText', 'Could be better...')
      } else if (clickPercent > 2 && clickPercent <= 3) {
        this.$set(this, 'clicksColour', average)
        this.$set(this.clicksEllipseOptions, 'color', average)
        this.$set(this.clicksEllipseOptions, 'font-color', average)
        this.$set(this, 'clicksText', 'Not bad')
      } else if (clickPercent > 3 && clickPercent <= 5) {
        this.$set(this, 'clicksColour', good)
        this.$set(this.clicksEllipseOptions, 'color', good)
        this.$set(this.clicksEllipseOptions, 'font-color', good)
        this.$set(this, 'clicksText', 'Great')
      } /* it is greater than 5 */ else {
        this.$set(this, 'clicksColour', great)
        this.$set(this.clicksEllipseOptions, 'color', great)
        this.$set(this.clicksEllipseOptions, 'font-color', great)
        this.$set(this, 'clicksText', 'Amazing!')
      }

      // Change our colour back to the primary colour for pro users
      this.$set(
        this.clicksEllipseOptions,
        'color',
        this.settings.primary_colour,
      )
      this.$set(this.opensEllipseOptions, 'color', this.settings.primary_colour)

      this.checkStatsHeight()

      this.$set(this, 'proData', true)
    },

    checkStatsHeight() {
      // Give the graphs time to render before checking the height
      setTimeout(() => {
        const statsContainer = document.getElementById('statsContainer')
        let height = statsContainer.scrollHeight + 8
        this.$set(this, 'statsHeight', height + 'px')
      }, 300)
    },

    handleUpgrade() {
      // if(this.settings.isPro) return;
      EventBus.$emit('showModal', {
        action: 'cw-registration',
        data: {
          reason: 3,
        },
      })
    },

    isActiveCampaign(id = null) {
      // If we have multiple campaigns as active data, then 'All' is selected
      // Else if we have a single campaign, we check it's ID against the active data
      const isMultiple = this.stats.data.stats.current_campaign_ids.length > 1
      if (!id && isMultiple) {
        return true
      } else if (
        this.stats.data.stats.current_campaign_ids.includes(id) &&
        !isMultiple
      ) {
        return true
      }

      return false
    },
  },
}
</script>

<style scoped>
.newsletter-preview * {
  font-family: 'Helvetica';
}

/* Tailwind not working on these column classes for some reason */
.newsletter-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
}

/* Tailwind not working on these column classes for some reason */
.newsletter-grid .col-span-2 {
  grid-column: span 2 / span 2 !important;
}

.newsletter-title {
  font-size: 30px;
  font-weight: 600;
}

.newsletter-body-text {
  font-size: 12px;
  color: #656d78;
  margin: 0;
}

.newsletter-post-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.clamp-subject {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.seperator-right::after {
  content: '';
  position: absolute;
  right: -6px;
  top: 50%;
  height: 14px;
  width: 1px;
  transform: translateY(-50%);
  background: #e5e7eb;
}

.upgrade-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -70%);
}

.btn.btn-cw-900.upgrade-button {
  font-weight: 400;
}
</style>
